import { BaseActionObject, StateNodeConfig, assign } from "xstate4"
import {
  Context,
  EditPlanSchema,
  StudyPlanTargets,
  Transitions
} from "../../types"
import {
  lessonDetailsDrawerState,
  addLessonDrawerState,
  planSettingsDrawerState,
  modifySectionSecondsDrawerState
} from "../drawers"
import {
  convertToAdaptive,
  convertToManual,
  removeSectionTransition
} from "./transitionalStates"
import {
  toggleBreakAfterLessonTransition,
  toggleBreakAfterSectionTransition,
  toggleBreakBeforeLessonTransition,
  toggleBreakBeforeSectionTransition
} from "../toggles/transitionalStates"
import { idleState, reorderModeState } from "./states"
import {
  confirmLeaveState,
  confirmConvertingPlanState,
  confirmFirstSavingTransition
} from "../modals"
import { setRedirectUrl } from "../../actions/setRedirectUrl"

export const editPlan: StateNodeConfig<
  Context,
  EditPlanSchema,
  Transitions,
  BaseActionObject
> = {
  entry: [
    /* Only set redirect URL if we're not already in edit mode,
    prevents setting url to edit when we invoke edit states like undo/redo transitions that leave this state and return
    */
    assign((ctx, evt, meta) => {
      if (ctx.previousParentState !== "edit_plan") {
        return setRedirectUrl(ctx, evt, meta, StudyPlanTargets.Edit)
      }
    }),
    /* We cannot empty expanded here, because some of the actions target another state. */
    assign((_ctx, _evt) => {
      return {
        previousParentState: "edit_plan",
        currentTarget: StudyPlanTargets.Edit,
        isReorderMode: false
      }
    })
  ],
  exit: assign((_ctx, _evt) => ({
    hasUnsavedStudyPlanChanges: false
  })),
  initial: "loading",
  states: {
    loading: {
      always: [
        {
          // If upon entering "edit_plan" state there is no draft plan, fetch it then return to "edit_plan"
          target: "#StudyPlan.build_populated_study_plan_transition",
          cond: ctx => !ctx.studyPlan && !!ctx.subject
        },
        {
          // If upon entering "edit_plan" state there is no draft plan or subject, redirect to landing
          target: "#StudyPlan.landing_view",
          cond: ctx =>
            !ctx.studyPlan && !ctx.subject && !ctx.isStudyPlanV2Enabled
        },
        {
          target: "idle"
        }
      ]
    },
    go_back_to_previous: {
      type: "history",
      history: "deep"
    },
    remove_section_transition: { ...removeSectionTransition },
    toggle_break_before_section_transition: {
      ...toggleBreakBeforeSectionTransition
    },
    toggle_break_after_section_transition: {
      ...toggleBreakAfterSectionTransition
    },
    toggle_break_before_lesson_transition: {
      ...toggleBreakBeforeLessonTransition
    },
    toggle_break_after_lesson_transition: {
      ...toggleBreakAfterLessonTransition
    },
    idle: { ...idleState },
    add_lesson: { ...addLessonDrawerState },
    modify_section_seconds: { ...modifySectionSecondsDrawerState },
    plan_settings: { ...planSettingsDrawerState },
    reorder_mode: { ...reorderModeState },
    confirm_converting_plan: { ...confirmConvertingPlanState },
    confirm_first_saving: { ...confirmFirstSavingTransition },
    confirm_leave: { ...confirmLeaveState },
    lesson_detail_view: { ...lessonDetailsDrawerState },
    convert_to_adaptive: { ...convertToAdaptive },
    convert_to_manual: { ...convertToManual }
  }
}
